import { useEffect, useState } from 'react'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SlackApiError } from '@assembly/slack'
import { PlatformProvider, usePlatform, type Platform } from '../platform'
import { useAccounts, useActiveAccount } from '../state/accounts'
import { useUi } from '../state/ui'
import { WorkspaceProvider, useWorkspace } from '../data/workspace'
import { useConversations } from '../data/hooks'
import { useRealtime } from '../data/realtime'
import { usePersistLastMessages, useResolveUsers, useSidebarSync, useUnreadScan } from '../state/sidebar'
import { useSignIn } from '../auth'
import { ChannelView, ThreadPanel } from './ChannelView'
import { Logo, Icon } from './Icon'
import { ModalHost } from './Modals'
import { ChannelDetailsPanel, ProfilePanel } from './Panels'
import { SearchView } from './SearchView'
import { Sidebar, WorkspaceRail } from './Sidebar'
import { SignIn } from './SignIn'
import { Spinner, useIsNarrow } from './common'

const SESSION_ERRORS = new Set(['invalid_auth', 'token_revoked', 'account_inactive', 'not_authed', 'token_expired'])

function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (err) => {
        if (err instanceof SlackApiError && SESSION_ERRORS.has(err.code)) {
          const { activeId, remove } = useAccounts.getState()
          if (activeId) {
            remove(activeId)
            useUi.getState().showToast('Your Slack session ended. Please sign in again.', 'error')
          }
        }
      },
    }),
    defaultOptions: {
      queries: {
        retry: (count, err) => !(err instanceof SlackApiError && (SESSION_ERRORS.has(err.code) || err.code === 'missing_scope' || err.code === 'channel_not_found')) && count < 2,
        refetchOnWindowFocus: false,
      },
    },
  })
}

function Toast() {
  const toast = useUi((s) => s.toast)
  if (!toast) return null
  return (
    <div className={`toast ${toast.tone ?? ''}`} role="status">
      {toast.tone === 'error' ? <Icon name="info" size={16} /> : <Icon name="check" size={16} />}
      {toast.text}
    </div>
  )
}

function HomeView() {
  const openModal = useUi((s) => s.openModal)
  const setView = useUi((s) => s.setView)
  const { account } = useWorkspace()
  return (
    <section className="home-view">
      <div className="home-inner">
        <Logo size={44} />
        <h1>{account.teamName}</h1>
        <p className="muted">Pick a conversation from the sidebar, or:</p>
        <div className="home-actions">
          <button type="button" className="button" onClick={() => openModal({ type: 'newMessage' })}>
            <Icon name="compose" size={16} /> New message
          </button>
          <button type="button" className="button" onClick={() => openModal({ type: 'browseChannels' })}>
            <Icon name="hash" size={16} /> Browse channels
          </button>
          <button type="button" className="button" onClick={() => setView({ type: 'search', query: '' })}>
            <Icon name="search" size={16} /> Search
          </button>
        </div>
      </div>
    </section>
  )
}

const isEditable = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))

/** Global keyboard shortcuts (desktop + web). */
function useShortcuts() {
  const { data: convs } = useConversations()
  const accounts = useAccounts((s) => s.accounts)
  const setActive = useAccounts((s) => s.setActive)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const ui = useUi.getState()
      const mod = e.metaKey || e.ctrlKey
      const k = e.key.toLowerCase()
      const switcherOpen = ui.modal?.type === 'switcher'
      if (mod && !e.shiftKey && (k === 'k' || k === 't')) {
        // Quick switcher; pressing it again closes it.
        e.preventDefault()
        ui.openModal(ui.modal?.type === 'switcher' ? null : { type: 'switcher' })
      } else if ((mod && !e.shiftKey && k === 'g') || (mod && e.shiftKey && k === 'f')) {
        e.preventDefault()
        ui.openModal(null)
        ui.setView({ type: 'search', query: '' })
      } else if (switcherOpen) {
        // The switcher owns the keyboard (Ctrl-N/P move its selection).
      } else if (mod && k === 'n') {
        e.preventDefault()
        ui.openModal({ type: 'newMessage' })
      } else if (mod && e.shiftKey && k === 'l') {
        e.preventDefault()
        ui.openModal({ type: 'browseChannels' })
      } else if (mod && k === '/') {
        e.preventDefault()
        ui.openModal({ type: 'shortcuts' })
      } else if (mod && /^[1-9]$/.test(e.key) && accounts[Number(e.key) - 1]) {
        e.preventDefault()
        setActive(accounts[Number(e.key) - 1].id)
      } else if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && convs?.length) {
        e.preventDefault()
        const items = [...document.querySelectorAll<HTMLButtonElement>('.sidebar-section .sidebar-item:not(.subtle)')]
        const idx = items.findIndex((el) => el.classList.contains('active'))
        const next = items[(idx + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]
        next?.click()
      } else if (!mod && !e.altKey && e.key.length === 1 && e.key !== ' ' && !ui.modal && !isEditable(e.target)) {
        // Typing with nothing focused goes to the message box, like Slack.
        const composer = document.querySelector<HTMLTextAreaElement>('.main .composer:not(.composer-editing) textarea')
        if (composer && !composer.disabled) composer.focus()
      } else if (e.key === 'Escape' && !ui.modal && !ui.editing && ui.panel && !(e.target as HTMLElement).closest('.popover')) {
        ui.openPanel(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [convs, accounts, setActive])
}

function usePrefersDark() {
  const [dark, setDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setDark(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return dark
}

function Workspace() {
  const platform = usePlatform()
  const { account } = useWorkspace()
  const narrow = useIsNarrow()
  const channelId = useUi((s) => s.channelId)
  const view = useUi((s) => s.view)
  const panel = useUi((s) => s.panel)
  const { data: convs } = useConversations()
  const [restored, setRestored] = useState(false)
  useRealtime()
  useShortcuts()
  useSidebarSync()
  useUnreadScan()
  useResolveUsers()
  usePersistLastMessages()

  // Restore the last open conversation for this workspace.
  const lastKey = `assembly.lastChannel.${account.id}`
  const recentKey = `assembly.recent.${account.id}`
  useEffect(() => {
    useUi.getState().reset()
    setRestored(false)
    Promise.all([platform.storage.get(lastKey), platform.storage.get(recentKey).catch(() => null)]).then(([id, recent]) => {
      try {
        if (recent) useUi.getState().setRecent(JSON.parse(recent))
      } catch {}
      if (id) useUi.getState().openChannel(id)
      if (id && narrow) useUi.getState().goHome()
      setRestored(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id])
  useEffect(() => {
    if (channelId) platform.storage.set(lastKey, channelId)
  }, [channelId, lastKey, platform])
  const recent = useUi((s) => s.recent)
  useEffect(() => {
    if (restored) platform.storage.set(recentKey, JSON.stringify(recent))
  }, [recent, recentKey, restored, platform])
  // First run: open #general on wide screens.
  useEffect(() => {
    if (restored && !channelId && !narrow && convs) {
      const general = convs.find((c) => c.is_general) ?? convs.find((c) => c.is_channel)
      if (general) useUi.getState().openChannel(general.id)
    }
  }, [restored, channelId, narrow, convs])

  const main =
    view.type === 'search' ? <SearchView initialQuery={view.query} /> : view.type === 'channel' && channelId ? <ChannelView channel={channelId} /> : <HomeView />

  const side = panel ? (
    panel.type === 'thread' ? (
      <ThreadPanel key={`${panel.channel}:${panel.ts}`} channel={panel.channel} ts={panel.ts} />
    ) : panel.type === 'details' ? (
      <ChannelDetailsPanel channel={panel.channel} tab={panel.tab} />
    ) : (
      <ProfilePanel userId={panel.user} />
    )
  ) : null

  const screen = side ? 'panel' : view.type === 'home' ? 'sidebar' : 'main'
  const prefersDark = usePrefersDark()
  useEffect(() => {
    platform.setChrome?.(narrow && screen === 'sidebar' ? 'dark' : prefersDark ? 'dark' : 'light')
  }, [platform, narrow, screen, prefersDark])

  if (narrow) {
    // Phone: one stacked screen at a time.
    return (
      <div className={`app narrow screen-${screen}`}>
        {screen === 'sidebar' && <Sidebar />}
        {screen === 'main' && <main className="main">{main}</main>}
        {screen === 'panel' && <div className="panel-screen">{side}</div>}
        <ModalHost />
        <Toast />
      </div>
    )
  }

  return (
    <div className={`app wide ${side ? 'with-panel' : ''}`}>
      <WorkspaceRail />
      <Sidebar />
      <main className="main">{main}</main>
      {side}
      <ModalHost />
      <Toast />
    </div>
  )
}

function Root() {
  const platform = usePlatform()
  const hydrated = useAccounts((s) => s.hydrated)
  const account = useActiveAccount()
  const { complete } = useSignIn()
  const [authError, setAuthError] = useState<string | null>(null)
  const [finishing, setFinishing] = useState(false)

  useEffect(() => {
    useAccounts.getState().hydrate(platform.storage).then(() => {
      const redirect = platform.consumeAuthRedirect?.()
      if (!redirect) return
      if ('error' in redirect) setAuthError(redirect.error)
      else {
        setFinishing(true)
        complete(redirect)
          .catch((e) => setAuthError(String(e.message ?? e)))
          .finally(() => setFinishing(false))
      }
    })
  }, [platform, complete])

  if (!hydrated || finishing) {
    return (
      <div className="splash">
        <Logo size={48} />
        {finishing && <Spinner />}
      </div>
    )
  }
  if (!account) {
    return (
      <>
        <SignIn initialError={authError} />
        <Toast />
      </>
    )
  }
  return (
    <WorkspaceProvider key={account.id} account={account}>
      <Workspace />
    </WorkspaceProvider>
  )
}

const queryClient = createQueryClient()

export function App({ platform }: { platform: Platform }) {
  useEffect(() => {
    document.documentElement.dataset.platform = platform.kind
  }, [platform.kind])
  // Effects run after the first successful render: tells the shell this UI build works.
  useEffect(() => platform.ready?.(), [platform])
  return (
    <PlatformProvider platform={platform}>
      <QueryClientProvider client={queryClient}>
        <Root />
      </QueryClientProvider>
    </PlatformProvider>
  )
}
